import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);
const STORAGE_KEY = "elora_cart";

const lineKey = (handle, variantId) => `${handle}::${variantId || "default"}`;

// Guest cart lines (public product snapshots + quantities) — no credentials or PII stored.
function loadLocal() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch (err) {
    logger.warn("Could not read saved cart", err);
    return [];
  }
}

function toLines(items) {
  return items.map((it) => ({
    product_id: it.product.handle,
    variant_id: it.variantId,
    quantity: it.quantity,
  }));
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(loadLocal); // [{product, variantId, quantity}]
  const syncedFor = useRef(null);
  const hydrated = useRef(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // Merge/pull server cart on login
  useEffect(() => {
    if (!user || !user.id) {
      syncedFor.current = null;
      hydrated.current = false;
      return;
    }
    if (syncedFor.current === user.id) return;
    syncedFor.current = user.id;
    (async () => {
      try {
        const { data } = await api.get("/cart");
        const server = (data.items || []).map((it) => ({
          product: it.product,
          variantId: it.variant_id,
          quantity: it.quantity,
        }));
        setItems((local) => {
          const map = new Map();
          for (const it of server) map.set(lineKey(it.product.handle, it.variantId), it);
          for (const it of local) {
            const k = lineKey(it.product.handle, it.variantId);
            if (map.has(k)) map.get(k).quantity = Math.max(map.get(k).quantity, it.quantity);
            else map.set(k, it);
          }
          const merged = Array.from(map.values());
          api.put("/cart", { items: toLines(merged) }).catch(() => {});
          return merged;
        });
      } catch (err) {
        logger.warn("Cart sync failed; keeping local bag", err);
      } finally {
        hydrated.current = true;
      }
    })();
  }, [user]);

  // Persist to server on every change while authenticated (only after initial merge)
  useEffect(() => {
    if (user && user.id && hydrated.current) {
      api.put("/cart", { items: toLines(items) }).catch(() => {});
    }
  }, [items, user]);

  const addItem = useCallback((product, variantId, quantity = 1) => {
    setItems((prev) => {
      const k = lineKey(product.handle, variantId);
      const existing = prev.find((it) => lineKey(it.product.handle, it.variantId) === k);
      if (existing) {
        return prev.map((it) =>
          lineKey(it.product.handle, it.variantId) === k
            ? { ...it, quantity: Math.min(99, it.quantity + quantity) }
            : it
        );
      }
      return [...prev, { product, variantId, quantity }];
    });
    toast("Added to bag", { description: product.title });
  }, []);

  const updateQuantity = useCallback((key, quantity) => {
    setItems((prev) =>
      prev
        .map((it) => (lineKey(it.product.handle, it.variantId) === key ? { ...it, quantity } : it))
        .filter((it) => it.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((it) => lineKey(it.product.handle, it.variantId) !== key));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const { count, subtotal } = useMemo(() => {
    let c = 0;
    let s = 0;
    for (const it of items) {
      c += it.quantity;
      s += (it.product.price || 0) * it.quantity;
    }
    return { count: c, subtotal: s };
  }, [items]);

  const value = useMemo(
    () => ({ items, count, subtotal, addItem, updateQuantity, removeItem, clear, lineKey }),
    [items, count, subtotal, addItem, updateQuantity, removeItem, clear]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
